import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import useBlurAutoSave from '../shared/hooks/useBlurAutoSave';
import { fieldCommitDateInputKeyUp } from '../shared/utils/fieldCommitOnEnterKeyDown';
import DetailSaveStatus from './DetailSaveStatus';
import { useAuth } from '../contexts/AuthContext';
import { getAssetLabel } from '../shared/utils/assetLabel';
import { getProjectProgressFillColor, getProjectProgressTooltipTitle } from '../shared/utils/projectProgressColor';
import { navigateToAiProjectBuilderForExistingProject } from '../shared/utils/aiProjectBuilderNavigate';
import { IconClose, IconReopen, IconAiAddTasks } from '../shared/icons/IconLib';
import LinkifiedEditableRead from './LinkifiedEditableRead';


import { apiFetch } from "../shared/api/apiFetch";

function formatProjectDate(value) {
  if (!value || !String(value).trim()) return '—';
  const d = dayjs(String(value).slice(0, 10));
  return d.isValid() ? d.format('MM/DD/YYYY') : '—';
}

function normalizeStatusLikeTask(value) {
  return value && String(value).trim() === 'Closed' ? 'Closed' : 'Open';
}

export default function CollapsibleProjectHeader({
  project,
  totalTasks,
  closedTasks,
  vendorLabel,
  assetLabel,
  vendorList,
  assetList,
  accountId,
  serverUrl,
  onAfterSave,
  onNotify,
  /** When set, closing the project (Open → Closed) opens this flow (e.g. parent confirm + preview) instead of calling the API here. */
  onRequestCloseProject,
  defaultCollapsed = false,
  storageKey,
}) {
  const navigate = useNavigate();
  useAuth();

  const [collapsed, setCollapsed] = useState(() => {
    if (!storageKey) return !!defaultCollapsed;
    try {
      const raw = sessionStorage.getItem(storageKey);
      if (raw === '1') return true;
      if (raw === '0') return false;
    } catch {
      // ignore storage errors
    }
    return !!defaultCollapsed;
  });

  const projectId = project?._id != null ? String(project._id) : '';

  const makeInitialValues = useCallback((p) => {
    const start = p?.start_date ? String(p.start_date).slice(0, 10) : '';
    const end = p?.end_date ? String(p.end_date).slice(0, 10) : '';
    return {
      name: (p?.name != null ? String(p.name) : '').trim(),
      is_template: p?.is_template === true,
      start_date: start,
      end_date: end,
      vendor_id: p?.vendor_id != null ? String(p.vendor_id).trim() : '',
      asset_id: p?.asset_id != null ? String(p.asset_id).trim() : '',
      status: normalizeStatusLikeTask(p?.status),
      notes: p?.notes != null ? String(p.notes) : '',
    };
  }, []);

  const [activeField, setActiveField] = useState('');
  const [initialValues, setInitialValues] = useState(() => makeInitialValues(project));
  const [draft, setDraft] = useState(() => makeInitialValues(project));

  useEffect(() => {
    const nextInit = makeInitialValues(project);
    setInitialValues(nextInit);
    setDraft(nextInit);
    setActiveField('');
  }, [projectId, makeInitialValues, project]);

  const hasChanges = useMemo(() => {
    return Object.keys(initialValues || {}).some((k) => (draft || {})[k] !== (initialValues || {})[k]);
  }, [draft, initialValues]);

  const persistChanges = useCallback(async () => {
    if (!accountId || !serverUrl || !projectId) return;
    const nameTrim = String(draft.name || '').trim();
    if (!nameTrim) return;
    const payload = {
      account_id: accountId,
      _id: projectId,
      name: nameTrim,
      is_template: draft.is_template === true,
      start_date: String(draft.start_date || '').trim(),
      end_date: String(draft.end_date || '').trim(),
      asset_id: String(draft.asset_id || '').trim(),
      vendor_id: String(draft.vendor_id || '').trim(),
      status: normalizeStatusLikeTask(draft.status),
      notes: draft.notes != null ? String(draft.notes) : '',
    };
    const resp = await apiFetch(serverUrl + '/updateProject', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
    const text = await resp.text();
    if (!resp.ok) {
      throw new Error(text || 'Failed to save project');
    }
    const nextInit = { ...draft, name: nameTrim, status: normalizeStatusLikeTask(draft.status) };
    setInitialValues(nextInit);
    setDraft(nextInit);
    setActiveField('');
    if (typeof onAfterSave === 'function') onAfterSave();
  }, [accountId, serverUrl, projectId, draft, onAfterSave]);

  const toggleProjectStatus = useCallback(async () => {
    const prevStatus = normalizeStatusLikeTask(draft?.status);
    const nextStatus = prevStatus === 'Closed' ? 'Open' : 'Closed';
    const nextDraft = { ...draft, status: nextStatus };
    setDraft(nextDraft);
    try {
      const resp = await apiFetch(serverUrl + (nextStatus === 'Closed' ? '/closeProject' : '/reopenProject'), {
        method: 'POST',
        body: JSON.stringify({
          account_id: accountId,
          _id: projectId,
        }),
      });
      const ct = (resp.headers.get('content-type') || '').toLowerCase();
      const body = ct.includes('application/json') ? await resp.json().catch(() => null) : await resp.text();
      if (!resp.ok) {
        const msg =
          typeof body === 'string'
            ? body
            : (body && body.message ? body.message : null);
        throw new Error(msg || 'Failed to update status');
      }
      setInitialValues(nextDraft);
      setDraft(nextDraft);
      setActiveField('');
      if (typeof onAfterSave === 'function') onAfterSave();
    } catch (e) {
      // revert UI draft if save fails
      setDraft(initialValues);
      throw e;
    }
  }, [accountId, projectId, serverUrl, draft, initialValues, onAfterSave]);

  const { isSaving, handleFieldBlur, handleFieldCommitKeyDown } = useBlurAutoSave({
    hasChanges,
    saveFn: persistChanges,
    onExitEdit: () => setActiveField(''),
  });

  const name = (draft?.name != null ? String(draft.name) : '').trim() || 'Unknown project';
  const status = normalizeStatusLikeTask(draft?.status);
  const startDate = formatProjectDate(draft?.start_date);
  const endDate = formatProjectDate(draft?.end_date);
  const notes = (draft?.notes != null ? String(draft.notes) : '').trim() || '—';

  const vendorDisplay = useMemo(() => {
    const vid = String(draft?.vendor_id || '').trim();
    if (!vid) return '—';
    const v = (vendorList || []).find((x) => String(x?._id) === vid);
    if (!v) return '—';
    return (v?.name || 'Vendor') + (v?.type ? ` (${v.type})` : '');
  }, [draft?.vendor_id, vendorList]);

  const assetDisplay = useMemo(() => {
    const aid = String(draft?.asset_id || '').trim();
    if (!aid) return '—';
    const a = (assetList || []).find((x) => String(x?._id) === aid);
    return a ? getAssetLabel(a) : '—';
  }, [draft?.asset_id, assetList]);

  const progressPct = useMemo(() => {
    const t = Number(totalTasks) || 0;
    const c = Number(closedTasks) || 0;
    if (t <= 0) return 0;
    return Math.max(0, Math.min(100, Math.round((c / t) * 100)));
  }, [totalTasks, closedTasks]);

  const progressLabel = useMemo(() => {
    const t = Number(totalTasks) || 0;
    const c = Number(closedTasks) || 0;
    if (t <= 0) return 'No tasks';
    return `${progressPct}% (${c}/${t})`;
  }, [totalTasks, closedTasks, progressPct]);

  const progressFillColor = useMemo(
    () => getProjectProgressFillColor(closedTasks, totalTasks, progressPct),
    [closedTasks, totalTasks, progressPct],
  );

  const progressTooltip = useMemo(
    () => getProjectProgressTooltipTitle(closedTasks, totalTasks, progressPct),
    [closedTasks, totalTasks, progressPct],
  );

  const toggle = () => {
    setCollapsed((prev) => {
      const next = !prev;
      if (storageKey) {
        try {
          sessionStorage.setItem(storageKey, next ? '1' : '0');
        } catch {
          // ignore storage errors
        }
      }
      return next;
    });
  };

  const goToAiProjectBuilder = useCallback(() => {
    if (!projectId) return;
    const start = String(draft.start_date || '').trim();
    const end = String(draft.end_date || '').trim();
    navigateToAiProjectBuilderForExistingProject(navigate, {
      _id: projectId,
      name: (draft.name != null ? String(draft.name) : '').trim(),
      start_date: start ? `${start}T00:00:00.000Z` : '',
      end_date: end ? `${end}T00:00:00.000Z` : '',
    });
  }, [navigate, projectId, draft.name, draft.start_date, draft.end_date]);

  return (
    <div className="panel project-header-panel" style={{ marginBottom: 12 }}>
      <button
        type="button"
        className="project-header-toggle"
        onClick={toggle}
        aria-expanded={!collapsed}
      >
        <span className="project-header-toggle__title">PROJECT DETAILS ({name.toUpperCase()})</span>
        <span style={{ marginLeft: 'auto' }}>
          <DetailSaveStatus isSaving={isSaving} hasChanges={hasChanges} />
        </span>
        <span className="project-header-toggle__chev" aria-hidden="true">
          {collapsed ? '▸' : '▾'}
        </span>
      </button>

      {!collapsed && (
        <div className="project-header-grid" role="region" aria-label="Project details">
          <div className="project-header-field">
            <div className="project-header-label">Name</div>
            {activeField === 'name' ? (
              <input
                autoFocus
                className="input input-full"
                type="text"
                value={draft.name}
                onChange={(e) => setDraft((p) => ({ ...p, name: e.target.value }))}
                onBlur={handleFieldBlur}
                onKeyDown={handleFieldCommitKeyDown}
              />
            ) : (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
                <button
                  type="button"
                  className="btn-link"
                  onClick={() => setActiveField('name')}
                  style={{ textAlign: 'left', padding: '4px 0', fontWeight: 500, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                  title={name}
                >
                  {name}
                </button>
              </div>
            )}
          </div>
          <div className="project-header-field">
            <div className="project-header-label">Status</div>
            <div className="project-header-value" style={{ fontWeight: 600 }}>
              {status}
            </div>
          </div>

          <div className="project-header-field">
            <div className="project-header-label">Start Date</div>
            {activeField === 'start_date' ? (
              <input
                autoFocus
                className="input input-full"
                type="date"
                value={draft.start_date || ''}
                onChange={(e) => setDraft((p) => ({ ...p, start_date: e.target.value }))}
                onBlur={handleFieldBlur}
                onKeyDown={handleFieldCommitKeyDown}
                onKeyUp={fieldCommitDateInputKeyUp}
              />
            ) : (
              <button type="button" className="btn-link" onClick={() => setActiveField('start_date')} style={{ textAlign: 'left', padding: '4px 0', fontWeight: 500 }}>
                {startDate}
              </button>
            )}
          </div>
          <div className="project-header-field">
            <div className="project-header-label">End Date</div>
            {activeField === 'end_date' ? (
              <input
                autoFocus
                className="input input-full"
                type="date"
                value={draft.end_date || ''}
                onChange={(e) => setDraft((p) => ({ ...p, end_date: e.target.value }))}
                onBlur={handleFieldBlur}
                onKeyDown={handleFieldCommitKeyDown}
                onKeyUp={fieldCommitDateInputKeyUp}
              />
            ) : (
              <button type="button" className="btn-link" onClick={() => setActiveField('end_date')} style={{ textAlign: 'left', padding: '4px 0', fontWeight: 500 }}>
                {endDate}
              </button>
            )}
          </div>
          <div className="project-header-field">
            <div className="project-header-label">Overall Progress</div>
            <div className="project-header-progress" title={progressTooltip}>
              <div className="project-header-progress__bar" aria-hidden="true">
                <div
                  className="project-header-progress__fill"
                  style={{ width: `${progressPct}%`, backgroundColor: progressFillColor }}
                />
              </div>
              <div className="project-header-progress__text">{progressLabel}</div>
            </div>
          </div>
          <div className="project-header-field">
            <div className="project-header-label">Asset</div>
            {activeField === 'asset_id' ? (
              <select
                autoFocus
                className="input input-full"
                value={draft.asset_id || ''}
                onChange={(e) => setDraft((p) => ({ ...p, asset_id: e.target.value }))}
                onBlur={handleFieldBlur}
                onKeyDown={handleFieldCommitKeyDown}
              >
                <option value="">—</option>
                {(assetList || []).map((a) => (
                  <option key={a._id} value={a._id}>
                    {getAssetLabel(a)}
                  </option>
                ))}
              </select>
            ) : (
              <button type="button" className="btn-link" onClick={() => setActiveField('asset_id')} style={{ textAlign: 'left', padding: '4px 0', fontWeight: 500 }}>
                {assetDisplay || assetLabel || '—'}
              </button>
            )}
          </div>
          <div className="project-header-field project-header-field--vendor-below-start">
            <div className="project-header-label">Vendor</div>
            {activeField === 'vendor_id' ? (
              <select
                autoFocus
                className="input input-full"
                value={draft.vendor_id || ''}
                onChange={(e) => setDraft((p) => ({ ...p, vendor_id: e.target.value }))}
                onBlur={handleFieldBlur}
                onKeyDown={handleFieldCommitKeyDown}
              >
                <option value="">—</option>
                {(vendorList || []).map((v) => (
                  <option key={v._id} value={v._id}>
                    {(v?.name || 'Vendor') + (v?.type ? ` (${v.type})` : '')}
                  </option>
                ))}
              </select>
            ) : (
              <button type="button" className="btn-link" onClick={() => setActiveField('vendor_id')} style={{ textAlign: 'left', padding: '4px 0', fontWeight: 500 }}>
                {vendorDisplay || vendorLabel || '—'}
              </button>
            )}
          </div>
          <div className="project-header-field project-header-field--type">
            <div className="project-header-label">Project Type</div>
            <label style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontWeight: 500 }}>
              <input
                type="checkbox"
                checked={draft.is_template === true}
                onChange={(e) => setDraft((p) => ({ ...p, is_template: e.target.checked }))}
                onBlur={handleFieldBlur}
                disabled={isSaving}
              />
              Template
            </label>
          </div>
          <div className="project-header-notes-actions-row">
            <div className="project-header-field project-header-field--notes">
              <div className="project-header-label">Notes</div>
              {activeField === 'notes' ? (
                <textarea
                  autoFocus
                  className="input input-full"
                  rows={2}
                  value={draft.notes || ''}
                  onChange={(e) => setDraft((p) => ({ ...p, notes: e.target.value }))}
                  onBlur={handleFieldBlur}
                  onKeyDown={handleFieldCommitKeyDown}
                />
              ) : (
                <LinkifiedEditableRead
                  text={notes}
                  onBeginEdit={() => setActiveField('notes')}
                  style={{ textAlign: 'left', padding: '4px 0', fontWeight: 500, whiteSpace: 'pre-wrap' }}
                />
              )}
            </div>
            <div className="project-header-field project-header-field--actions-ai">
              <div className="project-header-label" style={{ visibility: 'hidden' }} aria-hidden="true">
                {'\u00a0'}
              </div>
              <button
                type="button"
                className="btn btn-sm btn-primary"
                style={{ width: '100%' }}
                title="Add tasks with AI"
                aria-label="Add tasks with AI"
                disabled={isSaving || !projectId}
                aria-disabled={isSaving || !projectId ? 'true' : 'false'}
                onClick={goToAiProjectBuilder}
              >
                <IconAiAddTasks size={16} style={{ marginRight: 4, verticalAlign: 'middle' }} />
                Add tasks (AI)
              </button>
            </div>
            <div
              className={
                'project-header-field project-header-field--actions-close'
              }
            >
              <div className="project-header-label" style={{ visibility: 'hidden' }} aria-hidden="true">
                {'\u00a0'}
              </div>
              <button
                type="button"
                className="btn btn-sm btn-primary"
                style={{ width: '100%' }}
                title={status === 'Closed' ? 'Reopen project' : 'Mark project complete'}
                aria-label={status === 'Closed' ? 'Reopen project' : 'Mark project complete'}
                disabled={isSaving}
                aria-disabled={isSaving ? 'true' : 'false'}
                onClick={() => {
                  if (status === 'Open' && typeof onRequestCloseProject === 'function') {
                    onRequestCloseProject();
                    return;
                  }
                  toggleProjectStatus().catch((e) => {
                    const msg = e && e.message ? e.message : 'Failed to update project status';
                    if (typeof onNotify === 'function') onNotify('Project status update failed', msg);
                  });
                }}
              >
                {status === 'Closed' ? (
                  <>
                    <IconReopen size={16} style={{ marginRight: 4, verticalAlign: 'middle' }} />
                    Reopen project
                  </>
                ) : (
                  <>
                    <IconClose size={16} style={{ marginRight: 4, verticalAlign: 'middle' }} />
                    Mark project complete
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

